import { useQuery } from '@tanstack/react-query';
import { type SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';

interface Report {
  tool: string;
  period: { from: string; to: string } | null;
  source: string;
  rows: Record<string, unknown>[];
}

interface Answer {
  conversationId: string;
  text: string;
  reports: Report[];
  verified: boolean;
  unverified: string[];
}

type Message = { role: 'user'; text: string } | ({ role: 'assistant' } & Answer);

const EXAMPLES = ['topSales', 'margin', 'runsOut', 'reorder', 'prices'] as const;

/** Ячейка отчёта: числа и строки как есть, вложенное — JSON. */
const cell = (value: unknown) =>
  value === null || value === undefined
    ? '—'
    : typeof value === 'string'
      ? value
      : JSON.stringify(value);

function ReportTable({ report }: { report: Report }) {
  const { t } = useTranslation();
  const columns = Object.keys(report.rows[0] ?? {});
  return (
    <details className="rounded border p-2 text-xs">
      <summary className="cursor-pointer text-muted-foreground">
        {t('asst.data')}: {report.period ? `${report.period.from} – ${report.period.to}` : ''} ·{' '}
        {report.source} · {t('asst.rows', { count: report.rows.length })}
      </summary>
      {report.rows.length ? (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c} className="px-1 text-left font-medium">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.rows.map((row, i) => (
                <tr key={i} className="border-t">
                  {columns.map((c) => (
                    <td key={c} className="px-1 py-0.5">
                      {cell(row[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </details>
  );
}

/** Ассистент админки: вопросы о продажах, марже, запасах — ответы по готовым отчётам. */
export function AssistantPage() {
  const { t, i18n } = useTranslation();
  const status = useQuery({
    queryKey: ['ai', 'assistant-status'],
    queryFn: () => api<{ enabled: boolean; configured: boolean }>('/ai/assistant/status'),
  });
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<string>();
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const ask = async (text: string) => {
    if (!text.trim() || busy) return;
    setError(undefined);
    setDraft('');
    setMessages((m) => [...m, { role: 'user', text }]);
    setBusy(true);
    try {
      const answer = await api<Answer>('/ai/assistant', {
        method: 'POST',
        body: {
          message: text,
          locale: i18n.language === 'ru' ? 'ru' : 'en',
          ...(conversationId ? { conversationId } : {}),
        },
      });
      setConversationId(answer.conversationId);
      setMessages((m) => [...m, { role: 'assistant', ...answer }]);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    void ask(draft);
  };

  return (
    <>
      <PageHeader title={t('asst.title')} description={t('asst.hint')} />
      {status.data && !status.data.enabled ? (
        <Alert className="mb-4">{status.data.configured ? t('asst.off') : t('asst.noKey')}</Alert>
      ) : null}
      <Card>
        <CardContent className="grid gap-4 pt-4 text-sm">
          {messages.length === 0 ? (
            <div className="flex flex-wrap gap-2">
              {EXAMPLES.map((e) => (
                <Button
                  key={e}
                  size="sm"
                  variant="outline"
                  disabled={!status.data?.enabled}
                  onClick={() => void ask(t(`asst.examples.${e}`))}
                >
                  {t(`asst.examples.${e}`)}
                </Button>
              ))}
            </div>
          ) : null}
          <div className="grid gap-3" aria-live="polite">
            {messages.map((m, i) =>
              m.role === 'user' ? (
                <p key={i} className="ml-auto max-w-xl rounded bg-muted px-3 py-2">
                  {m.text}
                </p>
              ) : (
                <div key={i} className="grid max-w-3xl gap-2" data-testid="assistant-answer">
                  <p className="whitespace-pre-line">{m.text}</p>
                  {!m.verified ? (
                    <Badge variant="destructive">
                      {t('asst.unverified', { numbers: m.unverified.join(', ') })}
                    </Badge>
                  ) : null}
                  {m.reports.map((r, j) => (
                    <ReportTable key={j} report={r} />
                  ))}
                </div>
              ),
            )}
            {busy ? <p className="text-muted-foreground">{t('asst.thinking')}</p> : null}
          </div>
          {error ? <Alert variant="destructive">{error}</Alert> : null}
          <form onSubmit={submit} className="flex gap-2">
            <Input
              aria-label={t('asst.question')}
              placeholder={t('asst.question')}
              value={draft}
              maxLength={2000}
              disabled={!status.data?.enabled}
              onChange={(e) => {
                setDraft(e.target.value);
              }}
            />
            <Button type="submit" disabled={busy || !draft.trim() || !status.data?.enabled}>
              {t('asst.ask')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </>
  );
}
